import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { usePageTracking } from "../hooks/useAnalytics";
import useUserStorybooks from "../hooks/useUserStorybooks";
import StoryShelf from "../components/library/PictureBook";
import CharacterSelectGrid from "../components/audiobook/CharacterSelectGrid";
import { getGenerationEstimate } from "../utils/audiobookEstimate";
import { saveCreationDraft, takeCreationDraft } from "../utils/creationDraft";
import {
  DEFAULT_PAGE_COUNT,
  DEFAULT_READING_LEVEL,
  MAX_STORYBOOK_CHARACTERS,
  READING_LEVEL_OPTIONS,
  STORY_LENGTH_OPTIONS,
  buildAudiobookCreateFormData,
  extractAudiobookId,
  extractRemainingStars,
  getStoryLengthStarCost,
} from "../utils/audiobookCreate";

const HERO_ROTATION_MS = 6500;
const SHELF_COLLAPSE_THRESHOLD = 5;
const DRAFT_KEY = "storybook-template";

const PAGE_COUNT_SUBTITLES = {
  8: "A short bedtime read",
  25: "A proper story",
  50: "A big adventure",
};

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const PencilIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const UserIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const SpeakerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M11 5L6 9H2v6h4l5 4V5z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);

const SparkleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2l1.6 5.8L19.5 9.5l-5.9 1.7L12 17l-1.6-5.8L4.5 9.5l5.9-1.7L12 2z" />
  </svg>
);

const BoltIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

const ChevronRightIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const FeatureChip = ({ icon: Icon, label }) => (
  <div
    className="inline-flex items-center gap-2 px-3.5 py-2"
    style={{
      ...FREDOKA,
      background: "#FFFFFF",
      color: "#0B1A33",
      fontWeight: 600,
      fontSize: 13,
      border: "2px solid rgba(11,26,51,0.08)",
      borderRadius: 999,
      boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
    }}
  >
    <span className="inline-flex h-5 w-5 items-center justify-center flex-shrink-0" style={{ color: "#0065F8" }}>
      <Icon className="h-4 w-4" />
    </span>
    <span className="leading-tight">{label}</span>
  </div>
);

/** Topic tile — the same curated art the Story topic picker uses. */
const TopicCard = ({ topic, onSelect }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(topic?.image_url) && !imgFailed;
  return (
    <button
      type="button"
      onClick={() => onSelect(topic)}
      className="group relative flex w-full flex-col overflow-hidden text-left transition-all duration-200 hover:-translate-y-1"
      style={{
        background: "#FFFFFF",
        border: "2px solid rgba(11,26,51,0.06)",
        borderRadius: 22,
        boxShadow: "0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)",
      }}
    >
      <div
        className="relative flex h-[170px] w-full items-center justify-center overflow-hidden sm:h-[190px]"
        style={{ background: "linear-gradient(180deg, #EAF3FF 0%, #DCEBFF 100%)" }}
      >
        {showImage && (
          <img
            src={topic.image_url}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImgFailed(true)}
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-3 p-3.5">
        <div className="min-w-0">
          <p className="truncate text-sm sm:text-base" style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700 }}>
            {topic?.name}
          </p>
          <p className="mt-0.5 truncate text-xs" style={{ ...FREDOKA, color: "#5A6B85" }}>
            A brand-new picture book every time
          </p>
        </div>
        <span
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center transition-all"
          style={{ background: "#0065F8", color: "#FFFFFF", borderRadius: 999, boxShadow: "0px 2px 0px #073B8E" }}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </span>
      </div>
    </button>
  );
};

const OptionPill = ({ selected, disabled, label, sub, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex flex-col items-start px-4 py-2.5 transition-all active:scale-[0.98] disabled:opacity-50"
    style={{
      ...FREDOKA,
      background: selected ? "#0065F8" : "#FFFFFF",
      color: selected ? "#FFFFFF" : "#0B1A33",
      border: `2px solid ${selected ? "#0065F8" : "rgba(11,26,51,0.10)"}`,
      borderRadius: 16,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    <span style={{ fontWeight: 700, fontSize: 14 }}>{label}</span>
    <span style={{ fontSize: 11, opacity: 0.75 }}>{sub}</span>
  </button>
);

/**
 * Storybook starter picker.
 *
 * Mirrors /story-template: pick a topic, choose the cast and the shape of the
 * book in one modal, and the book is queued straight from there. The curated
 * topic catalogue and artwork are shared with Stories; only the premise
 * differs, sparked with `medium: "storybook"` so it is written for illustrated
 * pages rather than animation clips.
 *
 * The manual wizard at /audiobooks/create is untouched and still reachable —
 * it is what the hero CTA opens.
 */
const StorybookTemplatePage = () => {
  usePageTracking("Storybook Template");
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user, updateUser } = useAuth();
  const { currentProfile } = useProfile();
  const { requireEntitlement } = useEntitlementGate();
  const { books, loading: booksLoading } = useUserStorybooks();

  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState("");
  const [showAllBooks, setShowAllBooks] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);

  // Starter modal: cast, length and reading level are all picked here, then the
  // book is created without a second screen.
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_COUNT);
  const [readingLevel, setReadingLevel] = useState(DEFAULT_READING_LEVEL);
  const [isCreating, setIsCreating] = useState(false);

  // Selections saved when the members-only wall interrupted a create; restored
  // once topics and characters have loaded so checkout returns to the same book.
  const draftRef = useRef(takeCreationDraft(DRAFT_KEY));
  const draftRestoredRef = useRef(false);
  const castSeededRef = useRef(false);

  const starCost = getStoryLengthStarCost(pageCount);

  const loadTopics = useCallback(async () => {
    if (!apiClient) return;
    setTopicsError("");
    setTopicsLoading(true);
    try {
      const response = await apiClient.getStoryTopics();
      const ok = response?.status === true || response?.status === "success";
      const data = Array.isArray(response?.data) ? response.data : [];
      if (!ok || !data.length) {
        throw new Error(
          response?.error || response?.detail || "We couldn't load story ideas right now."
        );
      }
      setTopics(data);
      setHeroIndex(0);
    } catch (error) {
      setTopicsError(error?.message || "We couldn't load story ideas right now.");
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  const loadCharacters = useCallback(async () => {
    if (!apiClient || !user?.id) return;
    try {
      const response = await apiClient.getUserCharacters(user.id, currentProfile?.id);
      const ok = response?.status === true || response?.status === "success";
      setCharacters(ok && Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setCharacters([]);
    }
  }, [apiClient, user?.id, currentProfile?.id]);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  // Reopen the interrupted topic once its catalogue entry is back.
  useEffect(() => {
    const draft = draftRef.current;
    if (!draft?.topicKey || draftRestoredRef.current || !topics.length) return;
    const topic = topics.find((t) => t.key === draft.topicKey);
    if (!topic) return;
    draftRestoredRef.current = true;
    setPageCount(draft.pageCount || DEFAULT_PAGE_COUNT);
    setReadingLevel(draft.readingLevel || DEFAULT_READING_LEVEL);
    setSelectedTopic(topic);
  }, [topics]);

  // Characters load separately from topics, so the cast is seeded once they
  // arrive: the saved cast when a wall-interrupted create is being resumed,
  // otherwise the same first-character default a fresh modal open uses.
  useEffect(() => {
    if (!characters.length || castSeededRef.current) return;
    castSeededRef.current = true;
    const saved = characters.filter((c) =>
      (draftRef.current?.selectedCharacterIds || []).includes(c.id)
    );
    setSelectedCharacters(saved.length ? saved : characters.slice(0, 1));
  }, [characters]);

  const heroImages = useMemo(
    () => topics.map((t) => t?.image_url).filter(Boolean),
    [topics]
  );

  useEffect(() => {
    if (heroImages.length < 2) return undefined;
    const id = setInterval(
      () => setHeroIndex((i) => (i + 1) % heroImages.length),
      HERO_ROTATION_MS
    );
    return () => clearInterval(id);
  }, [heroImages.length]);

  useEffect(() => {
    document.body.style.overflow = selectedTopic ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedTopic]);

  const startFromScratch = useCallback(() => {
    navigate("/audiobooks/create");
  }, [navigate]);

  // Everything needed to pick the interrupted creation back up after checkout.
  const saveDraft = useCallback(
    (topic, extra = {}) =>
      saveCreationDraft(DRAFT_KEY, {
        topicKey: topic?.key,
        selectedCharacterIds: selectedCharacters.map((c) => c.id),
        pageCount,
        readingLevel,
        ...extra,
      }),
    [selectedCharacters, pageCount, readingLevel]
  );

  const handleOpenTopic = useCallback(
    (topic) => {
      if (!topic?.key) return;
      if (!requireEntitlement({ trigger: "creation", contentType: "storybook" })) {
        saveCreationDraft(DRAFT_KEY, {
          topicKey: topic.key,
          pageCount: DEFAULT_PAGE_COUNT,
          readingLevel: DEFAULT_READING_LEVEL,
        });
        return;
      }
      setPageCount(DEFAULT_PAGE_COUNT);
      setReadingLevel(DEFAULT_READING_LEVEL);
      setSelectedCharacters(characters.slice(0, 1));
      setSelectedTopic(topic);
    },
    [requireEntitlement, characters]
  );

  const handleCloseModal = useCallback(() => {
    if (isCreating) return;
    setSelectedTopic(null);
    setSelectedCharacters([]);
  }, [isCreating]);

  const toggleCharacter = useCallback((character) => {
    setSelectedCharacters((prev) => {
      if (prev.some((c) => c.id === character.id)) {
        return prev.filter((c) => c.id !== character.id);
      }
      if (prev.length >= MAX_STORYBOOK_CHARACTERS) return prev;
      return [...prev, character];
    });
  }, []);

  // Create straight from the modal: spark a storybook-shaped premise for the
  // topic, then queue it through the same /audiobooks/create the wizard uses.
  const handleCreateFromModal = useCallback(async () => {
    if (!apiClient || !user?.id || !selectedTopic?.key || isCreating) return;
    if (selectedCharacters.length === 0) {
      toast.error("Pick at least one character");
      return;
    }
    if (!requireEntitlement({ trigger: "creation", contentType: "storybook" })) {
      saveDraft(selectedTopic);
      return;
    }

    const currentStars = user?.pixley_stars ?? 0;
    if (currentStars < starCost) {
      toast.error(
        `You need ${starCost} Pixley Stars for a ${pageCount}-page storybook. You have ${currentStars}.`
      );
      return;
    }

    setIsCreating(true);
    try {
      const spark = await apiClient.sparkStoryTopic(selectedTopic.key, {
        medium: "storybook",
        pageCount,
        readingLevel,
      });
      const premise = spark?.data?.premise;
      if (!premise) {
        throw new Error(
          spark?.error || spark?.detail || "Couldn't dream up a story idea — try again!"
        );
      }

      const formData = buildAudiobookCreateFormData({
        userId: user.id,
        characters: selectedCharacters,
        title: spark?.data?.title || "",
        description: premise,
        isPublic: false,
        pageCount,
        readingLevel,
      });

      const response = await apiClient.createAudiobook(formData);
      const audiobookId = extractAudiobookId(response);
      if (!audiobookId) {
        throw new Error(response?.error || response?.detail || "Failed to create storybook");
      }

      const remaining = extractRemainingStars(response);
      if (remaining !== undefined) updateUser({ ...user, pixley_stars: remaining });

      // The creation completed, so any wall-era draft is stale.
      takeCreationDraft(DRAFT_KEY);
      draftRef.current = null;

      toast.success(
        `Your storybook will be ready in ${getGenerationEstimate(pageCount)}! We'll notify you when it's ready.`,
        { duration: 8000 }
      );
      setIsCreating(false);
      setSelectedTopic(null);
      setSelectedCharacters([]);
      navigate(`/storybooks/${audiobookId}`);
    } catch (error) {
      setIsCreating(false);
      toast.error(error?.message || "Could not create your storybook right now.");
    }
  }, [
    apiClient,
    user,
    selectedTopic,
    selectedCharacters,
    pageCount,
    readingLevel,
    starCost,
    isCreating,
    requireEntitlement,
    saveDraft,
    updateUser,
    navigate,
  ]);

  const heroImage = heroImages[heroIndex] || null;

  const renderTopicGrid = () => {
    if (topicsLoading) {
      return (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse"
              style={{ height: 250, background: "rgba(11,26,51,0.06)", borderRadius: 22 }}
            />
          ))}
        </div>
      );
    }

    if (topicsError) {
      return (
        <div
          className="flex flex-col items-start gap-3 p-5"
          style={{ background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.06)", borderRadius: 22 }}
        >
          <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 600, fontSize: 14 }}>{topicsError}</p>
          <button
            type="button"
            onClick={loadTopics}
            className="px-5 py-2.5 transition active:scale-[0.98]"
            style={{
              ...FREDOKA,
              background: "#0065F8",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 14,
              border: "none",
              borderRadius: 999,
              boxShadow: "0px 3px 0px #073B8E",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <>
        {/* Mobile: horizontal scroller */}
        <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 md:hidden" style={{ scrollSnapType: "x mandatory" }}>
          {topics.map((topic) => (
            <div key={topic.key} className="w-[72vw] flex-shrink-0 sm:w-[46vw]" style={{ scrollSnapAlign: "start" }}>
              <TopicCard topic={topic} onSelect={handleOpenTopic} />
            </div>
          ))}
        </div>

        {/* Desktop: grid */}
        <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
          {topics.map((topic) => (
            <TopicCard key={topic.key} topic={topic} onSelect={handleOpenTopic} />
          ))}
        </div>
      </>
    );
  };

  return (
    <div
      className="storyverse-container pb-16 pt-24 sm:pt-28"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        color: "#0B1A33",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft brand blobs in the page background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          style={{
            position: "absolute", top: -160, left: -160, width: 460, height: 460,
            borderRadius: "50%", background: "rgba(0,101,248,0.07)", filter: "blur(30px)",
          }}
        />
        <div
          style={{
            position: "absolute", bottom: -200, right: -200, width: 520, height: 520,
            borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)",
          }}
        />
      </div>

      <div className="relative w-full px-4 sm:px-6 lg:px-10">
        {/* ── Hero: start a storybook from scratch ── */}
        <section
          role="button"
          tabIndex={0}
          onClick={startFromScratch}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              startFromScratch();
            }
          }}
          className="group relative cursor-pointer overflow-hidden transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 28,
            boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)",
          }}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[220px] overflow-hidden lg:inset-y-0 lg:h-auto">
            <AnimatePresence mode="wait">
              {heroImage && (
                <motion.img
                  key={heroImage}
                  src={heroImage}
                  alt=""
                  aria-hidden="true"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
            </AnimatePresence>
            {/* Mobile: fade image bottom into white */}
            <div
              className="absolute inset-0 lg:hidden"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 65%, #FFFFFF 100%)",
              }}
            />
            {/* Desktop: fade image left into white over the text column */}
            <div
              className="absolute inset-0 hidden lg:block"
              style={{
                background:
                  "linear-gradient(to right, #FFFFFF 0%, #FFFFFF 40%, rgba(255,255,255,0.92) 53%, rgba(255,255,255,0.55) 70%, rgba(255,255,255,0) 87%)",
              }}
            />
          </div>

          {/* Soft brand-accent halo behind the headline */}
          <div
            className="pointer-events-none absolute -top-32 left-1/4 h-[300px] w-[55%] -translate-x-1/2"
            style={{ borderRadius: "50%", background: "rgba(0,101,248,0.10)", filter: "blur(30px)" }}
          />
          <div
            className="pointer-events-none absolute -bottom-24 -right-16 h-[260px] w-[260px]"
            style={{ borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }}
          />

          <div className="relative z-10 px-6 pb-8 pt-[230px] sm:px-10 sm:pb-10 sm:pt-[250px] lg:max-w-[55%] lg:py-12 lg:pt-12">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: "0.04em",
                border: "2px solid rgba(0,101,248,0.22)",
                borderRadius: 999,
                boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
              }}
            >
              <SparkleIcon className="h-3.5 w-3.5" />
              <span>Best way to create</span>
            </div>

            <h1
              className="mt-5 sm:text-5xl md:text-6xl"
              style={{
                ...FREDOKA,
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4.5vw, 3.6rem)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
              }}
            >
              Create Your<br />
              <span style={{ color: "#0065F8" }}>Own Storybook</span>
            </h1>

            <p className="mt-4 max-w-md sm:text-base" style={{ ...FREDOKA, color: "#334155", fontSize: 14 }}>
              An illustrated, read-aloud picture book starring your own characters — written from your idea.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <FeatureChip icon={UserIcon} label="Star your own characters" />
              <FeatureChip icon={PencilIcon} label="Illustrated page by page" />
              <FeatureChip icon={SpeakerIcon} label="Narrated out loud" />
            </div>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                startFromScratch();
              }}
              className="mt-8 inline-flex items-center justify-center px-10 py-4 transition-all active:scale-[0.98]"
              style={{
                ...FREDOKA,
                background: "#0065F8",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 17,
                border: "none",
                borderRadius: 999,
                boxShadow: "0px 5px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)",
                cursor: "pointer",
              }}
            >
              Start Creating
            </button>
          </div>
        </section>

        {/* ── Quick start: pick a topic ── */}
        <section className="mt-10 space-y-5">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span
                className="inline-block mb-1"
                style={{
                  ...FREDOKA,
                  color: "#0065F8",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                Quick Start
              </span>
              <div className="flex items-center gap-2">
                <BoltIcon className="h-5 w-5" style={{ color: "#0065F8" }} />
                <h2
                  className="sm:text-2xl"
                  style={{
                    ...FREDOKA,
                    color: "#0B1A33",
                    fontWeight: 700,
                    fontSize: 22,
                    letterSpacing: "-0.01em",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  Need a quick idea?
                </h2>
              </div>
              <p className="mt-1 text-sm" style={{ ...FREDOKA, color: "#5A6B85" }}>
                Pick a theme and we'll write a fresh story idea — you just choose the characters.
              </p>
            </div>
          </div>

          {renderTopicGrid()}
        </section>

        {/* ── Your storybooks ── */}
        <section className="mt-12">
          <div className="mb-1 flex items-end justify-between gap-4">
            <h2
              style={{
                ...FREDOKA,
                color: "#0065F8",
                fontWeight: 700,
                fontSize: "clamp(20px, 2.4vw, 26px)",
                letterSpacing: "-0.01em",
                margin: 0,
              }}
            >
              Your Storybooks
            </h2>
            {books.length > SHELF_COLLAPSE_THRESHOLD && (
              <button
                type="button"
                onClick={() => setShowAllBooks((v) => !v)}
                className="inline-flex items-center gap-1 transition"
                style={{
                  ...FREDOKA,
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 13,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {showAllBooks ? "Show less" : `View all (${books.length})`}
                <ChevronRightIcon
                  className="h-3.5 w-3.5"
                  style={{
                    transition: "transform 200ms",
                    transform: showAllBooks ? "rotate(-90deg)" : "rotate(90deg)",
                  }}
                />
              </button>
            )}
          </div>

          {booksLoading ? (
            <div className="flex gap-4 overflow-hidden pt-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse shrink-0"
                  style={{
                    width: "clamp(120px, 14vw, 160px)",
                    height: "clamp(160px, 20vw, 220px)",
                    background: "rgba(11,26,51,0.06)",
                    borderRadius: 12,
                  }}
                />
              ))}
            </div>
          ) : (
            <StoryShelf
              stories={books}
              expanded={showAllBooks}
              onStoryClick={(book) => navigate(`/audiobooks/${book.id}`)}
              onCreate={startFromScratch}
              emptyTitle="Your shelf is empty"
              emptySubtitle="Pick a theme above to fill it with picture books."
              emptyCtaLabel="+ Create your first storybook"
            />
          )}
        </section>
      </div>

      {/* ── Starter modal: cast + shape, then the book is queued from here ── */}
      <AnimatePresence>
        {selectedTopic && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ background: "rgba(11,26,51,0.45)", padding: 16 }}
            onClick={handleCloseModal}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={`Create a ${selectedTopic.name} storybook`}
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-lg overflow-hidden"
              style={{
                background: "#FFFFFF",
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 26,
                boxShadow: "0px 6px 0px rgba(11,26,51,0.08), 0px 20px 44px rgba(11,26,51,0.18)",
              }}
            >
              <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: "2px solid rgba(11,26,51,0.06)" }}>
                {selectedTopic.image_url && (
                  <img
                    src={selectedTopic.image_url}
                    alt=""
                    aria-hidden="true"
                    className="h-12 w-12 flex-shrink-0 object-cover"
                    style={{ borderRadius: 14 }}
                  />
                )}
                <div className="min-w-0">
                  <p className="truncate" style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 17 }}>
                    {selectedTopic.name}
                  </p>
                  <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12 }}>
                    We'll write a fresh idea for this theme
                  </p>
                </div>
              </div>

              <div
                className="space-y-6 px-5 py-5"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                <CharacterSelectGrid
                  characters={characters}
                  selectedCharacters={selectedCharacters}
                  onToggle={toggleCharacter}
                  maxSelected={MAX_STORYBOOK_CHARACTERS}
                  heading="Who stars in it?"
                  subheading={`Choose 1–${MAX_STORYBOOK_CHARACTERS} characters for this book.`}
                />

                <div>
                  <p className="mb-2" style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 13 }}>
                    How long?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {STORY_LENGTH_OPTIONS.map((option) => (
                      <OptionPill
                        key={option.value}
                        selected={pageCount === option.value}
                        disabled={isCreating}
                        label={option.label}
                        sub={PAGE_COUNT_SUBTITLES[option.value]}
                        onClick={() => setPageCount(option.value)}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-2" style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 13 }}>
                    Reading level
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {READING_LEVEL_OPTIONS.map((option) => (
                      <OptionPill
                        key={option.value}
                        selected={readingLevel === option.value}
                        disabled={isCreating}
                        label={option.label}
                        sub={option.sub}
                        onClick={() => setReadingLevel(option.value)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div
                className="flex items-center justify-between gap-2 px-5 py-4"
                style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }}
              >
                <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, fontWeight: 600 }}>
                  {starCost} stars
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    disabled={isCreating}
                    className="px-5 py-2.5 transition disabled:opacity-50"
                    style={{
                      ...FREDOKA,
                      background: "transparent",
                      color: "#5A6B85",
                      fontWeight: 700,
                      fontSize: 14,
                      border: "none",
                      borderRadius: 999,
                      cursor: isCreating ? "not-allowed" : "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateFromModal}
                    disabled={isCreating || selectedCharacters.length === 0}
                    className="inline-flex items-center gap-2 px-6 py-3 transition active:scale-[0.98] disabled:opacity-50"
                    style={{
                      ...FREDOKA,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 15,
                      border: "none",
                      borderRadius: 999,
                      boxShadow: "0px 4px 0px #073B8E",
                      cursor: isCreating ? "wait" : "pointer",
                    }}
                  >
                    {isCreating ? (
                      <>
                        <span
                          aria-hidden="true"
                          className="h-4 w-4 animate-spin"
                          style={{
                            border: "2px solid rgba(255,255,255,0.4)",
                            borderTopColor: "#FFFFFF",
                            borderRadius: "50%",
                          }}
                        />
                        Making your book…
                      </>
                    ) : (
                      "Create Storybook"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StorybookTemplatePage;
